import { AlertCircle } from '@untitled-ui/icons-react/build/cjs';
import React, { useState } from 'react';
import Radio from 'src/components/Radio/Radio';
import TooltipCmp from 'src/components/TooltipCmp';

interface IQuickActionRadioItem {
    id: string;
    value: string;
    labelTitle: string;
    isRequired?: boolean;
    labelSubtitle?: string;
    showErrorIcon?: boolean;
    // Optional rich content for the warning icon — when provided, the error icon shows this in a hover tooltip
    // (e.g. Order Quick Actions' name/reason/solution). When omitted, the icon stays a plain indicator.
    warningContent?: React.ReactNode;
    onChangeFunc: (e: React.ChangeEvent<HTMLInputElement>) => void;
    isChecked?: boolean;
}

interface IProps {
    items: IQuickActionRadioItem[];
    inputName: string;
}

const QuickActionRadioList = ({ items, inputName }: IProps) => {
    // Seed internal selection from whichever item the parent marks as checked on mount
    const [selectedValue, setSelectedValue] = useState<string | undefined>(() => items.find((item) => item.isChecked)?.value);

    return (
        <ul className="space-y-2 tooltip-content-with-scroll">
            {items.map(({ id, value, labelTitle, isRequired, labelSubtitle, showErrorIcon, warningContent, onChangeFunc }) => {
                const isSelected = selectedValue === value;

                return (
                    <li
                        key={id}
                        className={`rounded-md border p-3 flex gap-x-2 cursor-pointer ${isSelected ? 'border-textSecondary bg-gray700' : 'border-utilityGray200 hover:border-textSecondary'}`}
                        onClick={() => {
                            setSelectedValue(value);
                            onChangeFunc({ target: { value } } as React.ChangeEvent<HTMLInputElement>);
                        }}
                    >
                        {/* stopPropagation prevents the radio click bubbling to li's onClick, avoiding double onChangeFunc calls */}
                        <div className="contents" onClick={(e) => e.stopPropagation()}>
                            <Radio
                                id={id}
                                inputName={inputName}
                                value={value}
                                checked={isSelected}
                                onChangeFunc={(e) => {
                                    setSelectedValue(e.target.value);
                                    onChangeFunc(e);
                                }}
                                labelName={
                                    <div className="flex gap-x-2">
                                        <div className="flex-1 space-y-0.5">
                                            <div className="flex text-textSecondary text-xs font-semibold">
                                                {labelTitle}
                                                {isRequired && <span className="text-red-600 ms-[2px] leading-4 text-xs">*</span>}
                                            </div>
                                            {labelSubtitle && <div className="text-grayLight600 text-xs leading-[1.5] font-normal break-word-common">{labelSubtitle}</div>}
                                        </div>
                                        {showErrorIcon &&
                                            (warningContent ? (
                                                <div onClick={(e) => e.stopPropagation()} className="flex-none">
                                                    <TooltipCmp TooltipCustomClass="tooltipV3-content-wrap" message={warningContent}>
                                                        <AlertCircle className="w-4 h-4 text-fgErrorPrimary cursor-pointer flex-none" />
                                                    </TooltipCmp>
                                                </div>
                                            ) : (
                                                <AlertCircle className="w-4 h-4 text-fgErrorPrimary cursor-pointer flex-none" />
                                            ))}
                                    </div>
                                }
                                labelHtmlFor={id}
                                radioLabelClassName="!normal-case flex-1"
                                parentClassName="flex w-full"
                                inputWrapClassName="black-checkmark"
                            />
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};

export default QuickActionRadioList;
